import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { CONTACTS } from "../data";
import { useLang, STR } from "../i18n";
import { trackGenerateLead } from "../analytics";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

const inputClass =
  "w-full px-4 py-[15px] border border-[#d9e0e4] bg-white text-[#0D1B2A] text-sm placeholder:text-[#9aa6ae] focus:border-[#F18A00] focus:outline-none transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", company: "" });
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const { lang } = useLang();
  const t = STR[lang].contact;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.status !== "success") throw new Error("send failed");
      trackGenerateLead(form.subject);
      toast.success(t.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", company: "" });
      setPrivacy(false);
    } catch (err) {
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" className="bg-white py-[95px]" data-testid="contact-section">
      <div className="max-w-[1240px] mx-auto px-6 grid lg:grid-cols-2 gap-[65px]">
        <motion.div {...reveal}>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#F18A00] font-bold" data-testid="contact-eyebrow">
            {t.eyebrow}
          </p>
          <h2 className="font-display font-medium text-[30px] tracking-[-0.02em] text-[#0D1B2A] mt-[7px]" data-testid="contact-title">
            {t.titlePre} <span className="text-[#F18A00]">{t.titleAccent}</span>
          </h2>
          <span className="block w-[38px] h-0.5 bg-[#F18A00] mt-[15px]" aria-hidden="true" />
          <p className="mt-5 text-lg text-[#65717a] font-light leading-relaxed" data-testid="contact-intro">
            {t.intro}
          </p>

          <div className="mt-8 bg-[#f5f7f8] p-8" data-testid="contact-details">
            <h3 className="font-display font-medium uppercase text-base text-[#0D1B2A] mb-4">{t.cardTitle}</h3>
            <p className="text-sm text-[#4A4F55] py-1" data-testid="contact-address">{CONTACTS.address}</p>
            <p className="text-sm py-1">
              <a href={`tel:${CONTACTS.phone1.replace(/\s/g, "")}`} className="text-[#4A4F55] hover:text-[#F18A00] transition-colors" data-testid="contact-phone-1">{CONTACTS.phone1}</a>
              <span className="text-[#9aa6ae] mx-2">·</span>
              <a href={`tel:${CONTACTS.phone2.replace(/\s/g, "")}`} className="text-[#4A4F55] hover:text-[#F18A00] transition-colors" data-testid="contact-phone-2">{CONTACTS.phone2}</a>
            </p>
            <p className="text-sm py-1">
              <a href={`mailto:${CONTACTS.email}`} className="text-[#4A4F55] hover:text-[#F18A00] transition-colors" data-testid="contact-email">{CONTACTS.email}</a>
            </p>
            <p className="text-sm text-[#9aa6ae] py-1" data-testid="contact-vat">{CONTACTS.vat}</p>
          </div>

          <div className="mt-6 relative h-[260px] border border-[#e8edf0]" data-testid="contact-map">
            <iframe
              title="Mappa — Project Works, Nizza Monferrato"
              src="https://www.openstreetmap.org/export/embed.html?bbox=8.3422%2C44.7691%2C8.3622%2C44.7791&layer=mapnik&marker=44.7741%2C8.3522"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              data-testid="contact-map-iframe"
            />
          </div>
        </motion.div>

        <motion.form {...reveal} transition={{ ...reveal.transition, delay: 0.15 }} onSubmit={submit} className="grid gap-[13px] self-start" data-testid="contact-form">
          <input required minLength={2} value={form.name} onChange={set("name")} placeholder={t.namePh} className={inputClass} data-testid="contact-form-name" />
          <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputClass} data-testid="contact-form-email" />
          <input value={form.phone} onChange={set("phone")} placeholder={t.phonePh} className={inputClass} data-testid="contact-form-phone" />
          <input value={form.subject} onChange={set("subject")} placeholder={t.subjectPh} className={inputClass} data-testid="contact-form-subject" />
          <textarea
            required
            minLength={5}
            value={form.message}
            onChange={set("message")}
            placeholder={t.messagePh}
            className={`${inputClass} min-h-[160px] resize-y`}
            data-testid="contact-form-message"
          />
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={set("company")}
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
            data-testid="contact-form-company"
          />
          <label className="flex items-center gap-2 text-xs text-[#69737c] cursor-pointer" data-testid="contact-form-privacy">
            <input
              type="checkbox"
              required
              checked={privacy}
              onChange={(e) => setPrivacy(e.target.checked)}
              className="accent-[#F18A00] w-4 h-4"
              data-testid="contact-form-privacy-checkbox"
            />
            {t.privacyPre} <a href={lang === "en" ? "/en/privacy-policy/" : "/privacy-policy/"} className="underline hover:text-[#F18A00] transition-colors" data-testid="contact-form-privacy-link">{t.privacyLink}</a>.
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-form-submit"
            className="inline-flex items-center justify-center gap-2 bg-[#F18A00] text-white px-5 py-3.5 text-xs uppercase tracking-[0.06em] hover:bg-[#0D1B2A] disabled:opacity-60 transition-colors duration-300"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> {t.sending}
              </>
            ) : (
              <>
                {t.submit} <ArrowRight size={14} />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
